import { createContext, createElement, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { openStream, type Change } from '@/realtime/stream'

// 스트림은 하나만 연다. 화면은 자기가 보는 자원의 변경에만 반응해 그 자원을
// 다시 읽고, 스트림이 없거나 끊기면 polling 간격이 원래대로 돌아온다.

type Listener = (changes: Change[] | 'resync') => void

type Realtime = {
  connected: boolean
  subscribe: (listener: Listener) => () => void
}

const Ctx = createContext<Realtime>({ connected: false, subscribe: () => () => {} })

export function RealtimeProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  // 구독자 집합은 렌더와 무관한 하나의 객체다.
  const [listeners] = useState(() => new Set<Listener>())

  useEffect(() => {
    if (!enabled) return
    const close = openStream({
      onChange: (changes) => listeners.forEach((l) => l(changes)),
      onResync: () => listeners.forEach((l) => l('resync')),
      onConnected: setConnected,
    })
    return () => {
      close()
      setConnected(false)
    }
  }, [enabled, listeners])

  const value = useMemo<Realtime>(
    () => ({
      connected,
      subscribe: (listener) => {
        listeners.add(listener)
        return () => {
          listeners.delete(listener)
        }
      },
    }),
    [connected, listeners],
  )
  return createElement(Ctx.Provider, { value }, children)
}

/** 스트림이 붙어 있으면 느긋한 간격, 아니면 기본 간격이다. */
export function useFallback(baseMs: number, liveMs = 60000): number {
  return useContext(Ctx).connected ? liveMs : baseMs
}

/**
 * match 에 걸리는 변경이 오면(또는 resync 면) refetch 를 부른다.
 * delayMs 를 주면 그 시간 안의 변경을 한 번으로 모은다.
 */
export function useRefetchOn(match: (c: Change) => boolean, refetch: () => void, delayMs = 0): void {
  const { subscribe } = useContext(Ctx)
  const matchRef = useRef(match)
  const refetchRef = useRef(refetch)
  useEffect(() => {
    matchRef.current = match
    refetchRef.current = refetch
  })
  useEffect(() => {
    let timer: number | undefined
    const fire = () => {
      if (delayMs <= 0) {
        refetchRef.current()
        return
      }
      if (timer !== undefined) return
      timer = window.setTimeout(() => {
        timer = undefined
        refetchRef.current()
      }, delayMs)
    }
    const off = subscribe((changes) => {
      if (changes === 'resync' || changes.some((c) => matchRef.current(c))) fire()
    })
    return () => {
      off()
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [subscribe, delayMs])
}
